/**
 * 서버 연결 점검 (TASK-2026-012 CR-01 §1·§2·§4).
 *
 * 브라우저의 온라인 표시(`navigator.onLine`)는 **점검을 촉발하는 힌트**일 뿐이다. 연결이
 * 돌아온 뒤에도 거짓으로 남아 전송과 제출을 모두 막은 사례가 있다. 이 모듈은 같은 출처의
 * `GET /readyz`로 실제 도달성을 확인해 그 결과를 화면과 전송기에 함께 준다.
 *
 * 지키는 것:
 * - **점검 성공은 도달성뿐이다.** 세션 유효성·batch ACK·저장 완료를 뜻하지 않는다. 그것은
 *   세션 조회와 전송기 ACK가 따로 판정한다.
 * - **수동 버튼과 예약 일정은 같은 `check()`를 부른다.** 진행 중인 점검이 있으면 그 결과를
 *   나눠 가지므로 중복 요청이 없다.
 * - **비용을 제한한다.** `/readyz`는 저장소를 한 번 읽는다. 시작할 때 한 번, 그 뒤에는 연결
 *   안 됨이고 화면이 보일 때만 30초에서 시작해 최대 5분까지 늘어나는 간격으로 점검한다.
 *   연결된 동안에는 요청 실패가 보고될 때만, 수동 버튼은 5초 간격 안에서 한 번만 점검한다.
 */

export type ConnectionState = 'unknown' | 'checking' | 'connected' | 'disconnected';

/** 점검 요청 제한 시간. 넘으면 연결 안 됨으로 본다. */
export const CHECK_TIMEOUT_MS = 8_000;
/** 연결 안 됨일 때 다음 예약 점검까지의 첫 간격과 상한 (CR-01 §2). */
export const RECHECK_BASE_MS = 30_000;
export const RECHECK_MAX_MS = 300_000;
/** 요청 실패 보고로 인한 점검의 최소 간격. 실패가 몰려도 `/readyz`를 연달아 부르지 않는다. */
export const FAILURE_RECHECK_GAP_MS = 5_000;
/**
 * 수동 점검 버튼의 최소 간격 (REV-004). 점검이 빨리 실패해도 연타가 요청을 늘리지 않는다.
 * 간격 안의 누름은 직전 결과를 돌려준다.
 */
export const MANUAL_CHECK_GAP_MS = 5_000;
/**
 * 연결된 채 대기하는 활성 참가 화면의 유휴 점검 간격 (TASK-2026-012 CR-02).
 *
 * 보낼 기록이 없고 브라우저가 `offline` 이벤트를 내지 않으면, 연결된 뒤에는 무엇도 다시
 * 점검하지 않아 실제 단절이 화면에 남지 않았다(2026-10-01 iPad 시뮬레이터 관찰). 이 간격마다
 * Firestore를 읽지 않는 `/health`로 도달성만 확인한다.
 */
export const IDLE_CHECK_INTERVAL_MS = 60_000;

export type ConnectivityEvent = 'online' | 'offline' | 'visibilitychange';

export interface ConnectivityDeps {
  /** 실제 도달성 점검. 정상 응답이면 `true`. `signal`이 끊기면 거절해도 된다. */
  readonly probe: (signal: AbortSignal) => Promise<boolean>;
  /**
   * 연결된 채 대기할 때의 가벼운 도달성 점검(CR-02, 페이지에서는 `/health`). 없으면 유휴 점검을
   * 하지 않는다. 성공은 연결 판정을 새로 만들지 않고, 실패만 연결 안 됨으로 바꾼다.
   */
  readonly idleProbe?: (signal: AbortSignal) => Promise<boolean>;
  readonly hintOnline: () => boolean;
  readonly visible: () => boolean;
  readonly now: () => number;
  readonly setTimer: (run: () => void, ms: number) => unknown;
  readonly clearTimer: (handle: unknown) => void;
  /** 브라우저 이벤트를 듣는다. 돌려받은 함수로 그만 듣는다. */
  readonly listen: (event: ConnectivityEvent, handler: () => void) => () => void;
  readonly timeoutMs?: number;
}

export interface Connectivity {
  /** 화면 표시용 상태. 점검 중이면 `checking`이다. */
  state(): ConnectionState;
  /**
   * 전송·온라인 전용 동작에 쓸 판정.
   *
   * 마지막으로 **확정된** 점검 결과가 있으면 그것을, 없으면 브라우저 힌트를 따른다.
   * 점검 중에는 직전 확정 결과를 유지한다.
   */
  online(): boolean;
  /**
   * 온라인 전용 동작을 열어도 되는가 (REV-001). **실제 점검이 연결을 확인했을 때만** 참이다.
   * 점검 전(`unknown`)은 브라우저가 온라인이라고 해도 확인이 아니다.
   */
  confirmed(): boolean;
  /** 지금 점검한다. 진행 중이면 그 점검을 함께 기다린다. 예약·이벤트 경로가 쓴다. */
  check(): Promise<ConnectionState>;
  /** 참가자가 누른 점검. `MANUAL_CHECK_GAP_MS` 안에서는 새 요청 없이 직전 결과를 돌려준다. */
  requestCheck(): Promise<ConnectionState>;
  /** 서버 요청이 네트워크 오류로 실패했다. 필요하면 다시 점검한다. */
  reportFailure(): void;
  subscribe(listener: (state: ConnectionState) => void): () => void;
  /**
   * 연결된 채 대기하는 동안의 유휴 점검을 건다 (CR-02). `active()`가 참인 등록이 하나라도 있고,
   * 연결됨이며 화면이 보일 때만 `IDLE_CHECK_INTERVAL_MS`마다 `idleProbe`를 부른다. 돌려받은
   * 함수로 푼다. 여러 번 걸어도 주기는 하나다.
   */
  watchIdle(active: () => boolean): () => void;
  /** 브라우저 이벤트와 예약 점검을 건다. 여러 번 불러도 한 번만 건다. */
  start(): () => void;
}

export function createConnectivity(deps: ConnectivityDeps): Connectivity {
  const timeoutMs = deps.timeoutMs ?? CHECK_TIMEOUT_MS;
  let settled: 'unknown' | 'connected' | 'disconnected' = 'unknown';
  let current: ConnectionState = 'unknown';
  let inflight: Promise<ConnectionState> | undefined;
  let failures = 0;
  let timer: unknown;
  let lastCheckAt = Number.NEGATIVE_INFINITY;
  let starts = 0;
  let unlisten: (() => void)[] = [];
  const listeners = new Set<(state: ConnectionState) => void>();
  /** 간격 안에 들어온 실패 보고를 간격 끝으로 미룬 점검 (REV-002). */
  let deferred: unknown;
  /**
   * `/readyz` 점검의 세대 (CR-02). 유휴 점검은 시작할 때의 세대를 기억하고, 끝났을 때 그 사이
   * 새 `/readyz`가 시작됐으면 결과를 버린다. 오래된 실패가 최신 성공을 덮지 않게 한다.
   */
  let generation = 0;
  /** 마지막으로 서버에 닿으려 한 시각(`/readyz`·`/health` 모두). 숨김에서 돌아올 때 쓴다. */
  let lastProbeAt = Number.NEGATIVE_INFINITY;
  const idleWatchers = new Set<() => boolean>();
  let idleTimer: unknown;
  let idleInflight = false;

  function set(next: ConnectionState): void {
    if (next === current) {
      return;
    }
    current = next;
    for (const listener of listeners) {
      listener(next);
    }
  }

  function cancelTimer(): void {
    if (timer !== undefined) {
      deps.clearTimer(timer);
      timer = undefined;
    }
  }

  /** 연결 안 됨이고 화면이 보이며 일정이 걸려 있을 때만 다음 점검을 예약한다. */
  function schedule(): void {
    cancelTimer();
    if (starts === 0 || settled !== 'disconnected' || !deps.visible()) {
      return;
    }
    const wait = Math.min(RECHECK_MAX_MS, RECHECK_BASE_MS * 2 ** Math.max(0, failures - 1));
    timer = deps.setTimer(() => {
      timer = undefined;
      void check();
    }, wait);
  }

  function cancelIdle(): void {
    if (idleTimer !== undefined) {
      deps.clearTimer(idleTimer);
      idleTimer = undefined;
    }
  }

  function idleActive(): boolean {
    return [...idleWatchers].some((active) => active());
  }

  /** 연결됨이고 화면이 보이며 유휴 점검이 걸려 있을 때만 다음 유휴 점검을 예약한다 (CR-02). */
  function scheduleIdle(): void {
    cancelIdle();
    if (
      deps.idleProbe === undefined ||
      starts === 0 ||
      idleWatchers.size === 0 ||
      settled !== 'connected' ||
      inflight !== undefined ||
      idleInflight ||
      !deps.visible()
    ) {
      return;
    }
    idleTimer = deps.setTimer(() => {
      idleTimer = undefined;
      void runIdle();
    }, IDLE_CHECK_INTERVAL_MS);
  }

  async function runIdle(): Promise<void> {
    const idleProbe = deps.idleProbe;
    if (idleProbe === undefined || settled !== 'connected' || inflight !== undefined || !deps.visible() || !idleActive()) {
      scheduleIdle();
      return;
    }
    const startedIn = generation;
    idleInflight = true;
    lastProbeAt = deps.now();
    const ok = await probeOnce(idleProbe);
    idleInflight = false;
    // 그 사이 /readyz가 새로 시작됐거나 판정이 바뀌었으면 이 결과는 낡았다.
    if (generation !== startedIn || settled !== 'connected') {
      scheduleIdle();
      return;
    }
    if (ok) {
      // 도달성만 확인했다. 연결 판정·세션·ACK에 대해서는 아무것도 말하지 않는다.
      scheduleIdle();
      return;
    }
    // 연결 안 됨으로 바꾸고 기존 /readyz 복구 일정(30초부터)에 맡긴다.
    settled = 'disconnected';
    failures = 1;
    set('disconnected');
    schedule();
  }

  async function probeOnce(probe: (signal: AbortSignal) => Promise<boolean> = deps.probe): Promise<boolean> {
    const controller = new AbortController();
    const guard = deps.setTimer(() => {
      controller.abort();
    }, timeoutMs);
    try {
      const timedOut = new Promise<boolean>((resolve) => {
        controller.signal.addEventListener('abort', () => {
          resolve(false);
        });
      });
      return await Promise.race([probe(controller.signal), timedOut]);
    } catch {
      return false;
    } finally {
      deps.clearTimer(guard);
    }
  }

  function check(): Promise<ConnectionState> {
    if (inflight !== undefined) {
      return inflight;
    }
    cancelTimer();
    cancelIdle();
    generation += 1;
    lastCheckAt = deps.now();
    lastProbeAt = lastCheckAt;
    set('checking');
    inflight = (async () => {
      const ok = await probeOnce();
      settled = ok ? 'connected' : 'disconnected';
      failures = ok ? 0 : failures + 1;
      inflight = undefined;
      set(settled);
      schedule();
      scheduleIdle();
      return settled;
    })();
    return inflight;
  }

  function requestCheck(): Promise<ConnectionState> {
    if (inflight !== undefined) {
      return inflight;
    }
    if (deps.now() - lastCheckAt < MANUAL_CHECK_GAP_MS) {
      return Promise.resolve(settled);
    }
    return check();
  }

  function reportFailure(): void {
    // 이미 연결 안 됨이면 예약 일정이 맡고, 진행 중인 점검이 있으면 그 결과를 따른다.
    if (settled === 'disconnected' || inflight !== undefined) {
      return;
    }
    /*
     * 실패가 몰려도 점검은 간격을 둔다. 다만 간격 안의 실패를 **버리지 않고** 간격이 끝나는
     * 때로 미룬다 (REV-002). 버리면 연결 확인 직후의 실제 단절이 표시에 남지 않는다.
     */
    const wait = lastCheckAt + FAILURE_RECHECK_GAP_MS - deps.now();
    if (wait > 0) {
      deferred ??= deps.setTimer(() => {
        deferred = undefined;
        void check();
      }, wait);
      return;
    }
    void check();
  }

  /**
   * 힌트가 오프라인이거나 확정된 결과가 연결 안 됨이면 점검한다. 유휴 점검 중인 화면이 유휴 간격
   * 이상 숨어 있었다면 그동안 끊겼을 수 있으므로 곧바로 `/readyz`로 확인한다 (CR-02).
   *
   * 등록된 감시자 수가 아니라 **지금 활성인** 감시자가 있는지를 본다. 제출을 마친 화면처럼
   * 등록만 남은 감시자로는 `/readyz`(Firestore 읽기)를 부르지 않는다 (REV-CR02-001).
   */
  function recheckIfDoubtful(): void {
    const staleIdle = idleActive() && deps.now() - lastProbeAt >= IDLE_CHECK_INTERVAL_MS;
    if (!deps.hintOnline() || settled === 'disconnected' || staleIdle) {
      void check();
      return;
    }
    scheduleIdle();
  }

  function start(): () => void {
    starts += 1;
    if (starts === 1) {
      unlisten = [
        deps.listen('online', () => {
          void check();
        }),
        deps.listen('offline', () => {
          void check();
        }),
        deps.listen('visibilitychange', () => {
          if (deps.visible()) {
            recheckIfDoubtful();
          } else {
            // 숨은 탭은 점검하지 않는다. 다시 보이면 곧바로 점검한다.
            cancelTimer();
            cancelIdle();
          }
        }),
      ];
      /*
       * 시작할 때 한 번은 힌트와 관계없이 실제로 점검한다 (REV-001). 브라우저는 온라인이라고
       * 해도 서버가 닿지 않는 경우가 있고, 점검 전에는 온라인 전용 동작을 열지 않기 때문이다.
       * 참가 화면을 열 때마다 `/readyz` 1회(저장소 읽기 1건)다.
       */
      if (deps.visible()) {
        void check();
      }
    }
    let stopped = false;
    return () => {
      if (stopped) {
        return;
      }
      stopped = true;
      starts -= 1;
      if (starts === 0) {
        for (const stop of unlisten) {
          stop();
        }
        unlisten = [];
        cancelTimer();
        cancelIdle();
        if (deferred !== undefined) {
          deps.clearTimer(deferred);
          deferred = undefined;
        }
      }
    };
  }

  return {
    state: () => current,
    online: () => (settled === 'unknown' ? deps.hintOnline() : settled === 'connected'),
    confirmed: () => settled === 'connected',
    check,
    requestCheck,
    reportFailure,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    watchIdle(active) {
      idleWatchers.add(active);
      if (idleTimer === undefined) {
        scheduleIdle();
      }
      let released = false;
      return () => {
        if (released) {
          return;
        }
        released = true;
        idleWatchers.delete(active);
        if (idleWatchers.size === 0) {
          cancelIdle();
        }
      };
    },
    start,
  };
}

/** 페이지의 실제 점검: 캐시 없는 같은 출처 `GET /readyz`. */
export async function probeReadyz(signal: AbortSignal): Promise<boolean> {
  const response = await fetch('/readyz', { method: 'GET', cache: 'no-store', credentials: 'same-origin', signal });
  /*
   * 본문을 끝까지 읽어 요청을 닫는다. 읽지 않으면 브라우저가 이 요청을 계속 열린 것으로 두어
   * 연결을 붙잡는다(검사에서는 `networkidle`에 끝내 닿지 않았다). 본문은 작은 상태 JSON이다.
   */
  await response.arrayBuffer();
  return response.ok;
}

/**
 * 연결된 채 대기할 때의 가벼운 점검 (CR-02): 캐시 없는 같은 출처 `GET /health`.
 *
 * 프로세스 도달성만 본다. Firestore·세션·batch ACK를 증명하지 않으므로 연결 판정을 새로 열지
 * 않는다. 본문을 읽어 요청을 닫는다.
 */
export async function probeHealth(signal: AbortSignal): Promise<boolean> {
  const response = await fetch('/health', { method: 'GET', cache: 'no-store', credentials: 'same-origin', signal });
  await response.arrayBuffer();
  return response.ok;
}
